import type { Maybe } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XEntityPicker } from '@adornis/entity-picker/client/x-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/fonts/fonts.js';
import { UserRole } from '@ils/static/enums.js';
import { customElement, property } from 'lit/decorators.js';
import { subscribeSearchUser } from '../../api/user.js';
import { Or } from '../../db/filter/or.js';
import { ILSUser } from '../../db/ils-user.js';

/**
 * @element ils-user-selection
 */
@customElement('ils-user-selection')
export class IlsUserSelection extends XEntityPicker<typeof ILSUser> {
  @property({ attribute: false })
  selectedLocation = new RXController(this, '');

  constructor() {
    super();
    this.selectedLocation.observable.subscribe(value => {
      if (this.value.value) {
        const user = this._entity.value;
        const examRoles = user.roles
          .filter(role => [UserRole.EXAMCENTER, UserRole.EXAMCENTERMANAGER].includes(role.name))
          .flatMap(role => role.contexts);
        if (!examRoles.includes(value)) {
          void XSnackbar.show('Der Koordinator wurde entfernt, weil er nicht dieser Location zugeordnet ist.');
          this.value.next(null);
          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: null } }));
        }
      }
      const filters = [
        new StringAspect({ fieldName: 'roles.name', mode: 'contains', value: UserRole.EXAMCENTER }),
        new StringAspect({ fieldName: 'roles.name', mode: 'contains', value: UserRole.ADMIN }),
      ];
      if (value) {
        filters.push(new StringAspect({ fieldName: 'locationID', mode: 'equals', value }));
      }
      this.options.next({
        class: ILSUser,
        operation: subscribeSearchUser,
        filter: new AdornisFilter({
          searchFields: ['email', 'ecommerce.firstName', 'ecommerce.lastName'],
          aspects: [new Or({ fieldName: 'roles.name', filters })],
        }),
        selectionSet: { ecommerce: { firstName: 1, lastName: 1 }, email: 1 },
      });
    });
  }

  override renderString = (user: Maybe<ILSUser>) =>
    user ? `${user.ecommerce?.firstName} ${user.ecommerce?.lastName} (${user.email})` : '';
}
